import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { ArrowRight, Eye, EyeOff, Check } from 'lucide-react';
import BackButton from '../../components/BackButton';
import BrandLogo from '../../components/BrandLogo';

export default function RegisterPage() {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        organizationName: ''
    });
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const { register } = useAuth();
    const navigate = useNavigate();

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        
        if (formData.password.length < 12) {
            setError(t('auth.password_too_short'));
            return;
        }
        
        if (!formData.organizationName.trim()) {
            setError(t('auth.company_required'));
            return;
        }

        setLoading(true);
        const result = await register(
            formData.name,
            formData.email,
            formData.password,
            formData.organizationName.trim()
        );
        
        if (result.success) {
            navigate('/verify-email-required', {
                replace: true,
                state: {
                    email: result.email || formData.email,
                    emailSent: result.emailSent !== false,
                },
            });
        } else {
            const localized = result.errorCode
                ? t(`auth.errors.${result.errorCode}`, { defaultValue: result.error || t('auth.errors.generic') })
                : (result.error || t('auth.errors.generic'));
            setError(localized);
        }
        setLoading(false);
    };

    const features = [
        t('auth.branding_feature_free_cvs'),
        t('auth.branding_feature_ai_scoring'),
        t('auth.branding_feature_dashboard'),
        t('auth.branding_feature_support')
    ];

    return (
        <div className="min-h-screen flex" data-testid="register-page">
            {/* Left Panel - Branding */}
            <div className="hidden lg:flex lg:w-1/2 bg-[#002FA7] p-12 flex-col justify-between">
                <div>
                    <Link to="/" className="flex items-center gap-3">
                        <BrandLogo size={40} />
                        <span className="text-2xl font-bold text-white font-['Outfit']">GUIRETAL</span>
                    </Link>
                </div>
                
                <div className="space-y-8">
                    <h1 className="text-4xl font-bold text-white font-['Outfit'] leading-tight">
                        {t('auth.branding_register_title')}
                    </h1>
                    <p className="text-white/80 text-lg">
                        {t('auth.branding_register_subtitle')}
                    </p>
                    
                    <div className="space-y-3">
                        {features.map((feature, index) => (
                            <div key={index} className="flex items-center gap-3 text-white/90">
                                <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center">
                                    <Check className="w-4 h-4" />
                                </div>
                                <span>{feature}</span>
                            </div>
                        ))}
                    </div>
                </div>
                
                <p className="text-white/60 text-sm">
                    {t('landing.footer_copyright')}
                </p>
            </div>

            {/* Right Panel - Register Form */}
            <div className="flex-1 flex items-start justify-center p-4 sm:p-8 bg-[#F9FAFB] relative">
                <div className="hidden lg:block absolute top-6 left-6">
                    <BackButton to="/" data-testid="register-back-button" />
                </div>
                <div className="w-full max-w-md">
                    {/* Mobile: BackButton + Logo on the same row */}
                    <div className="lg:hidden mb-6 flex items-center justify-between">
                        <BackButton to="/" data-testid="register-back-button-mobile" />
                        <Link to="/" className="flex items-center gap-2">
                            <BrandLogo size={36} />
                            <span className="text-xl font-bold text-[#0A0A0A] font-['Outfit']">GUIRETAL</span>
                        </Link>
                    </div>

                    <div className="bg-white p-8 rounded-lg border border-[#E5E7EB] shadow-sm">
                        <div className="text-center mb-8">
                            <h2 className="text-2xl font-bold text-[#0A0A0A] font-['Outfit']">
                                {t('auth.register_title')}
                            </h2>
                            <p className="text-[#4B5563] mt-2">
                                {t('auth.register_subtitle')}
                            </p>
                        </div>

                        {error && (
                            <div className="mb-6 p-4 bg-[#FEE2E2] border border-[#EF4444]/20 rounded-lg" data-testid="register-error">
                                <p className="text-[#DC2626] text-sm">{error}</p>
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-5">
                            <div className="space-y-2">
                                <Label htmlFor="name" className="text-[#0A0A0A]">
                                    {t('auth.full_name')} <span className="text-[#EF4444]">*</span>
                                </Label>
                                <Input
                                    id="name"
                                    name="name"
                                    type="text"
                                    placeholder={t('auth.placeholder_name')}
                                    value={formData.name}
                                    onChange={handleChange}
                                    required
                                    className="border-[#E5E7EB] focus:border-[#002FA7] focus:ring-[#002FA7]/10"
                                    data-testid="register-name-input"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="email" className="text-[#0A0A0A]">
                                    {t('auth.email_pro')} <span className="text-[#EF4444]">*</span>
                                </Label>
                                <Input
                                    id="email"
                                    name="email"
                                    type="email"
                                    placeholder={t('auth.placeholder_email_register')}
                                    value={formData.email}
                                    onChange={handleChange}
                                    required
                                    className="border-[#E5E7EB] focus:border-[#002FA7] focus:ring-[#002FA7]/10"
                                    data-testid="register-email-input"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="organizationName" className="text-[#0A0A0A]">
                                    {t('auth.organization_name')} <span className="text-[#EF4444]">*</span>
                                </Label>
                                <Input
                                    id="organizationName"
                                    name="organizationName"
                                    type="text"
                                    placeholder={t('auth.placeholder_org')}
                                    value={formData.organizationName}
                                    onChange={handleChange}
                                    required
                                    className="border-[#E5E7EB] focus:border-[#002FA7] focus:ring-[#002FA7]/10"
                                    data-testid="register-org-input"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="password" className="text-[#0A0A0A]">
                                    {t('auth.password')} <span className="text-[#EF4444]">*</span>
                                </Label>
                                <div className="relative">
                                    <Input
                                        id="password"
                                        name="password"
                                        type={showPassword ? 'text' : 'password'}
                                        placeholder={t('auth.placeholder_password_min')}
                                        value={formData.password}
                                        onChange={handleChange}
                                        required
                                        className="border-[#E5E7EB] focus:border-[#002FA7] focus:ring-[#002FA7]/10 pr-10"
                                        data-testid="register-password-input"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#4B5563]"
                                    >
                                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                    </button>
                                </div>
                            </div>

                            <Button
                                type="submit"
                                disabled={loading}
                                className="w-full bg-[#002FA7] hover:bg-[#002480] text-white h-11"
                                data-testid="register-submit-btn"
                            >
                                {loading ? (
                                    <span className="flex items-center gap-2">
                                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                        </svg>
                                        {t('auth.sign_up_loading')}
                                    </span>
                                ) : (
                                    <span className="flex items-center gap-2">
                                        {t('auth.register_cta')}
                                        <ArrowRight className="w-4 h-4" />
                                    </span>
                                )}
                            </Button>

                            <p className="text-xs text-[#9CA3AF] text-center">
                                {t('auth.terms_acceptance')}{' '}
                                <Link to="/terms" target="_blank" rel="noopener noreferrer" className="text-[#002FA7] hover:underline" data-testid="register-terms-link">{t('landing.footer_terms')}</Link>
                                {' '}{t('common.and')}{' '}
                                <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="text-[#002FA7] hover:underline" data-testid="register-privacy-link">{t('auth.privacy_policy')}</Link>
                            </p>
                        </form>

                        <p className="text-center mt-6 text-[#4B5563]">
                            {t('auth.already_account')}{' '}
                            <Link to="/login" className="text-[#002FA7] font-medium hover:underline">
                                {t('auth.sign_in')}
                            </Link>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
